import { useEffect, useRef, useState } from "react";
import { COVER_TEXT_MAX, LIMITS } from "@instead/shared";
import { useT } from "../lib/i18n.ts";
import { gradientStyle } from "./Cover.tsx";
import { Ic } from "./Icons.tsx";
import { ChipGroup, Sheet } from "./Ui.tsx";

/** What the form holds about the cover until Save (D-27). */
export type CoverDraft =
  | { kind: "keep" }                               // whatever the item has now
  | { kind: "auto" }                               // link/source thumbnail or gradient
  | { kind: "none" }
  | { kind: "emoji"; value: string }
  | { kind: "text"; value: string }
  | { kind: "image"; dataUrl: string; lowRes: boolean }; // client-cropped, uploaded on save

const EMOJI: [string, string][] = [
  ["🎸", "guitar music"], ["📚", "book read"], ["🌿", "plant leaf nature"], ["🎨", "paint art"], ["🧘", "yoga meditate"], ["☕", "coffee tea"],
  ["🚲", "bike cycle"], ["🎧", "headphones music listen"], ["🍞", "bread bake"], ["🌊", "ocean sea wave"], ["✏️", "pencil write draw"], ["🧩", "puzzle"],
  ["🎹", "piano"], ["🪴", "plant pot"], ["🎬", "film movie"], ["📻", "radio podcast"], ["🧶", "yarn knit"], ["🍵", "tea"],
  ["🌙", "moon night"], ["☀️", "sun day"], ["🌳", "tree park"], ["🏞️", "park nature"], ["🚶", "walk"], ["🏃", "run"],
  ["🧺", "basket laundry"], ["🍳", "cook pan"], ["🥗", "salad cook"], ["📷", "camera photo"], ["🎮", "game"], ["♟️", "chess"],
  ["🃏", "cards"], ["🎲", "dice game"], ["🖌️", "brush paint"], ["🪡", "sew needle"], ["🔧", "fix tool"], ["🪵", "wood"],
  ["🐦", "bird"], ["🐈", "cat"], ["🐕", "dog"], ["🌸", "flower"], ["🍂", "leaf autumn"], ["❄️", "snow winter"],
  ["🛁", "bath"], ["🛏️", "bed sleep nap"], ["🕯️", "candle"], ["📝", "note journal write"], ["✉️", "letter write"], ["📖", "read book open"],
  ["🎵", "music note"], ["🎤", "sing"], ["🥁", "drum"], ["🎻", "violin"], ["🏊", "swim"], ["⛰️", "mountain hike"],
  ["🧭", "compass explore"], ["🗺️", "map"], ["🔭", "telescope stars"], ["🌌", "stars night sky"], ["🧪", "science"], ["🪐", "planet space"],
  ["🍫", "chocolate"], ["🍪", "cookie bake"], ["🍷", "wine"], ["🧊", "ice cold"], ["🔥", "fire"], ["💧", "water"],
  ["🎯", "target aim"], ["🏹", "archery"], ["🧗", "climb"], ["⛺", "camp tent"], ["🚣", "row boat"], ["🛶", "canoe"],
];

export function CoverMenu({ hasLink, linkThumb, onClose, onPick }: {
  hasLink: boolean;
  linkThumb: string | null;
  onClose: () => void;
  onPick: (d: CoverDraft | { kind: "file"; file: File }) => void;
}) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const [emoji, setEmoji] = useState(false);
  const [text, setText] = useState(false);
  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) onPick({ kind: "file", file: f });
  };
  if (emoji) return <EmojiSheet onClose={onClose} onPick={(v) => onPick({ kind: "emoji", value: v })} />;
  if (text) return <TextSheet onClose={onClose} onPick={(v) => onPick({ kind: "text", value: v })} />;
  return (
    <Sheet title={t("add.image")} onClose={onClose}>
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={pickFile} />
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickFile} />
      <button type="button" className="sheet-row" onClick={() => camRef.current?.click()}><span style={{ color: "var(--accent)" }}>{Ic.camera()}</span><span className="grow">{t("cover.photo")}</span></button>
      <button type="button" className="sheet-row" onClick={() => fileRef.current?.click()}><span style={{ color: "var(--accent)" }}>{Ic.library()}</span><span className="grow">{t("cover.library")}</span></button>
      {hasLink && linkThumb && (
        <button type="button" className="sheet-row" onClick={() => onPick({ kind: "auto" })}>
          <span style={{ width: 20, display: "inline-flex", justifyContent: "center" }}><span style={{ width: 20, height: 11, borderRadius: 3, background: "var(--line)" }} /></span>
          <span className="grow">{t("cover.fromLink")}</span>
          <img src={linkThumb} alt="" style={{ width: 56, height: 32, objectFit: "cover", borderRadius: 4, border: "1px solid var(--line)" }} />
        </button>
      )}
      <button type="button" className="sheet-row" onClick={() => setEmoji(true)}><span style={{ color: "var(--accent)" }}>{Ic.smile()}</span><span className="grow">{t("cover.emoji")}</span></button>
      <button type="button" className="sheet-row" onClick={() => setText(true)}><span style={{ color: "var(--accent)" }}>{Ic.text()}</span><span className="grow">{t("cover.text")}</span></button>
      <button type="button" className="sheet-row" onClick={() => onPick({ kind: "none" })}><span style={{ color: "var(--accent)" }}>{Ic.none()}</span><span className="grow">{t("cover.none")}</span></button>
    </Sheet>
  );
}

function EmojiSheet({ onClose, onPick }: { onClose: () => void; onPick: (v: string) => void }) {
  const t = useT();
  const [q, setQ] = useState("");
  const [v, setV] = useState("🎸");
  const list = EMOJI.filter(([, tags]) => !q.trim() || tags.includes(q.trim().toLowerCase()));
  return (
    <Sheet title={t("cover.emoji")} onClose={onClose}>
      <div className="cover cover-l" style={gradientStyle(11)} aria-hidden="true"><span className="cover-emoji">{v}</span></div>
      <div className="field" style={{ marginTop: "var(--sp-3)" }}>
        <label htmlFor="emoji-q" className="visually-hidden">{t("cover.searchEmoji")}</label>
        <input id="emoji-q" className="input" type="search" placeholder={t("cover.searchEmoji")} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="emoji-grid" role="listbox" aria-label={t("cover.emoji")}>
        {list.map(([e]) => (
          <button key={e} type="button" role="option" aria-selected={v === e} onClick={() => setV(e)}>{e}</button>
        ))}
      </div>
      <button type="button" className="btn btn-primary btn-block" style={{ marginTop: "var(--sp-3)" }} onClick={() => onPick(v)}>{t("use")}</button>
    </Sheet>
  );
}

function TextSheet({ onClose, onPick }: { onClose: () => void; onPick: (v: string) => void }) {
  const t = useT();
  const [v, setV] = useState("");
  return (
    <Sheet title={t("cover.text")} onClose={onClose}>
      <div className="cover cover-l" style={gradientStyle(23)} aria-hidden="true"><span className="cover-text">{v || " "}</span></div>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", marginTop: "var(--sp-3)" }}>
        <input className="input" style={{ flex: 1 }} aria-label={t("cover.text")} maxLength={COVER_TEXT_MAX} value={v} onChange={(e) => setV(e.target.value)} autoFocus />
        <span className="counter">{v.length} / {COVER_TEXT_MAX}</span>
      </div>
      <button type="button" className="btn btn-primary btn-block" style={{ marginTop: "var(--sp-3)" }} disabled={!v.trim()} onClick={() => onPick(v.trim())}>{t("use")}</button>
    </Sheet>
  );
}

/**
 * Crop to 16:9: drag to pan, slider to zoom. Zoomed out past the picture,
 * the exposed edges fill in — blur or tone — and the options appear only
 * then. Exports at most 960×540 (never upscales; low-res flagged).
 */
export function CropCover({ file, onCancel, onUse }: { file: File; onCancel: () => void; onUse: (d: CoverDraft) => void }) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1); // 1 = fill the frame
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [fill, setFill] = useState<"blur" | "tone">("blur");
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const W = LIMITS.coverWidth;
  const H = LIMITS.coverHeight;

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => setImg(i);
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const layout = () => {
    if (!img) return null;
    const fillScale = Math.max(W / img.naturalWidth, H / img.naturalHeight);
    const s = fillScale * zoom;
    const w = img.naturalWidth * s;
    const h = img.naturalHeight * s;
    const maxX = Math.max(0, (w - W) / 2);
    const maxY = Math.max(0, (h - H) / 2);
    const x = (W - w) / 2 + Math.max(-maxX, Math.min(maxX, off.x));
    const y = (H - h) / 2 + Math.max(-maxY, Math.min(maxY, off.y));
    return { x, y, w, h, edges: w < W - 0.5 || h < H - 0.5 };
  };

  const draw = (target: HTMLCanvasElement) => {
    const l = layout();
    if (!img || !l) return;
    const ctx = target.getContext("2d")!;
    ctx.clearRect(0, 0, W, H);
    if (l.edges) {
      if (fill === "blur") {
        ctx.save();
        ctx.filter = "blur(24px)";
        const s = Math.max(W / img.naturalWidth, H / img.naturalHeight) * 1.1;
        ctx.drawImage(img, (W - img.naturalWidth * s) / 2, (H - img.naturalHeight * s) / 2, img.naturalWidth * s, img.naturalHeight * s);
        ctx.restore();
      } else {
        const tone = document.createElement("canvas");
        tone.width = 1; tone.height = 1;
        tone.getContext("2d")!.drawImage(img, 0, 0, 1, 1);
        const [r, g, b] = tone.getContext("2d")!.getImageData(0, 0, 1, 1).data;
        ctx.fillStyle = `rgb(${r} ${g} ${b})`;
        ctx.fillRect(0, 0, W, H);
      }
    }
    ctx.drawImage(img, l.x, l.y, l.w, l.h);
  };

  useEffect(() => {
    if (canvasRef.current) draw(canvasRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, zoom, off, fill]);

  const l = layout();
  const lowRes = !!img && img.naturalWidth < W;

  function use() {
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    draw(c);
    onUse({ kind: "image", dataUrl: c.toDataURL("image/jpeg", 0.9), lowRes });
  }

  return (
    <Sheet onClose={onCancel} title={t("add.crop")} action={<button type="button" className="btn btn-primary" style={{ minHeight: 36 }} onClick={use} disabled={!img}>{t("use")}</button>}>
      <div className="crop-frame"
        onPointerDown={(e) => { drag.current = { x: e.clientX, y: e.clientY, ox: off.x, oy: off.y }; (e.target as Element).setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => {
          if (!drag.current || !canvasRef.current) return;
          const k = W / canvasRef.current.clientWidth;
          setOff({ x: drag.current.ox + (e.clientX - drag.current.x) * k, y: drag.current.oy + (e.clientY - drag.current.y) * k });
        }}
        onPointerUp={() => { drag.current = null; }}>
        <canvas ref={canvasRef} width={W} height={H} aria-label={t("add.crop")} />
      </div>
      <input className="crop-zoom" type="range" min={0.5} max={3} step={0.01} value={zoom} aria-label={t("add.crop")} onChange={(e) => setZoom(Number(e.target.value))} />
      {/* always laid out, shown only when edges are exposed: appearing must not move the picture (founder, 2026-09-16, D-43) */}
      <div className="crop-fill" style={{ marginTop: "var(--sp-3)", visibility: l?.edges ? "visible" : "hidden" }} inert={!l?.edges} aria-hidden={!l?.edges}>
        <div className="eyebrow">{t("cover.fill")}</div>
        <ChipGroup label={t("cover.fill")} selected={[fill]} allowNone={false}
          options={[{ value: "blur", label: t("cover.blur") }, { value: "tone", label: t("cover.tone") }]}
          onChange={(v) => setFill((v[0] as "blur" | "tone") ?? "blur")} />
      </div>
      {lowRes && (
        <div className="banner" role="status" style={{ marginTop: "var(--sp-3)" }}>
          <span className="dot warn-dot" aria-hidden="true" />
          <span>{t("cover.lowRes")}</span>
        </div>
      )}
    </Sheet>
  );
}

/** A data URL → Blob without fetch(): fetching a data: URL is a connect-src
 *  request, and the production CSP names no `data:` there — Safari reported
 *  it as "Load failed" and Save appeared to do nothing (D-43). */
export async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const comma = dataUrl.indexOf(",");
  const head = dataUrl.slice(0, comma);
  const type = head.slice(5).split(";")[0] || "application/octet-stream";
  const payload = dataUrl.slice(comma + 1);
  if (/;base64$/i.test(head)) {
    const bin = atob(payload);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type });
  }
  return new Blob([decodeURIComponent(payload)], { type });
}
